import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CameraIcon,
  PaperClipIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import { qualityManagementApi } from '../../api/qualityManagementApi';
import { useAuth } from '../../contexts/AuthContext';
import ResponsiveModal from '../ui/ResponsiveModal';

const WORKFLOW_STAGE_LABELS = {
  initiation: 'Başlatma',
  action: 'Aksiyon',
  approval: 'Onay',
  closing: 'Kapatma',
  completed: 'Tamamlandı',
  rejected: 'Reddedildi',
};

/** Single visible frame: legend border only; inner controls must stay borderless (see qmSelectStyles / qmFieldsetNativeClass). */
const QmFieldset = ({ legend, children, className = '', singleLine = false }) => (
  <fieldset
    className={`min-w-0 rounded-lg border border-slate-200 bg-white px-2.5 pb-2 pt-1 shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden ${className}`}
  >
    <legend className="block w-full max-w-full px-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
      {legend}
    </legend>
    <div
      className={
        singleLine
          ? 'min-w-0 flex min-h-[34px] items-center'
          : 'min-w-0 pt-0.5'
      }
    >
      {children}
    </div>
  </fieldset>
);

const qmFieldsetNativeClass =
  'w-full border-0 bg-transparent shadow-none ring-0 rounded-none text-xs text-gray-900 dark:text-gray-100 leading-snug outline-none focus:ring-0 focus:outline-none focus-visible:ring-0 disabled:opacity-60 dark:[color-scheme:dark]';

/** Tarih, tek satır metin, salt okunur dosya adı — select ile aynı yükseklik */
const qmSingleLineInputClass = `${qmFieldsetNativeClass} box-border h-[34px] min-h-[34px] max-h-[34px] py-0`;

/** Dosya / kamera — alan yüksekliği ile hizalı, kompakt */
const qmFileAttachBtnClass =
  'inline-flex h-7 min-h-7 shrink-0 items-center justify-center gap-1 rounded-md px-2 text-[10px] font-medium text-white shadow-sm disabled:opacity-50 sm:h-8 sm:min-h-8 sm:px-2.5 sm:text-xs';

const initialFormState = {
  df_definition: '',
  dep_code: '',
  df_initiator_department: '',
  df_receiver_department: '',
  df_initiator_full_name: '',
  df_receiver_full_name: '',
  opening_date: '',
  due_date: '',
  completion_date: '',
  related_document: '',
  related_document_name: '',
  nonconformity_description: '',
  nonconformity_description_attachment: '',
  nonconformity_summary: '',
  nonconformity_source: '',
  root_cause_description: '',
  root_cause_summary: '',
  corrective_action_description: '',
  corrective_action_description_attachment: '',
  closing_notes: '',
  approver_full_name: '',
  approval_decision: '',
  approval_comment: '',
  approval_confirmed: false,
  closing_department: '',
  closing_qm_user: '',
};

const QualityManagementFormModal = ({
  isOpen,
  onClose,
  onSave,
  onDone,
  initialData = null,
  dfDefinitions = [],
  hotels = [],
  departments = [],
  qmUsers = [],
  nonconformitySummaries = [],
  nonconformitySources = [],
  rootCauseSummaries = [],
  interactiveStage: interactiveStageProp = null,
}) => {
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [openStage, setOpenStage] = useState('stage1');
  const [autoDfNo, setAutoDfNo] = useState('');
  const [counterWarning, setCounterWarning] = useState('');
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [initiatorUserId, setInitiatorUserId] = useState('');
  const [receiverUserId, setReceiverUserId] = useState('');
  const [closingUserId, setClosingUserId] = useState('');
  const [closingUsersList, setClosingUsersList] = useState([]);
  const [closingUsersLoading, setClosingUsersLoading] = useState(false);
  const [relatedDocumentFile, setRelatedDocumentFile] = useState(null);
  const [relatedDocumentLabel, setRelatedDocumentLabel] = useState('');
  const [ncAttachmentFile, setNcAttachmentFile] = useState(null);
  const [ncAttachmentLabel, setNcAttachmentLabel] = useState('');
  const [caAttachmentFile, setCaAttachmentFile] = useState(null);
  const [caAttachmentLabel, setCaAttachmentLabel] = useState('');
  const documentInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const ncDocumentInputRef = useRef(null);
  const ncCameraInputRef = useRef(null);
  const caDocumentInputRef = useRef(null);
  const caCameraInputRef = useRef(null);

  const qmSelectStyles = useMemo(
    () => ({
      // No inner box: fieldset already draws the frame; kill default rs control border/bg.
      control: (base, state) => ({
        ...base,
        minHeight: 32,
        height: 32,
        minWidth: 0,
        borderWidth: 0,
        borderStyle: 'none',
        borderColor: 'transparent',
        boxShadow: state.isFocused ? 'inset 0 0 0 1px #3498DB' : 'none',
        backgroundColor: 'transparent',
        borderRadius: 0,
        cursor: 'default',
        '&:hover': {
          borderColor: 'transparent',
        },
      }),
      valueContainer: (base) => ({
        ...base,
        padding: '0 4px 0 2px',
        alignItems: 'center',
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: 28,
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  const qmSelectClassNames = {
    control: () =>
      '!min-h-[34px] !h-[34px] !text-xs !rounded-none !border-0 !shadow-none !bg-transparent',
    singleValue: () => '!text-xs text-gray-900 dark:text-gray-100',
    input: () => '!text-xs text-gray-900 dark:text-gray-100',
    placeholder: () => '!text-xs text-gray-400 dark:text-gray-500',
    menu: () =>
      '!text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg',
    menuList: () => 'py-1',
    option: (state) =>
      `!text-xs cursor-pointer px-2 py-1 ${
        state.isFocused ? 'bg-gray-100 dark:bg-gray-700' : ''
      } ${state.isSelected ? 'bg-highlight-500 text-white' : 'text-gray-900 dark:text-gray-100'}`,
  };

  const workflowStage = initialData?.workflow_stage || 'initiation';
  const permissions = useMemo(
    () =>
      initialData?.permissions || {
        can_edit_initiation: true,
        can_edit_action: false,
        can_submit_initiation: true,
        can_submit_action: false,
        can_approve: false,
        can_close: false,
        can_edit_approval_fields: false,
        can_edit_closing_fields: false,
        read_only_all: false,
        can_edit_opening_date_admin_only: false,
      },
    [initialData]
  );

  const screenMismatch = (stage) => Boolean(interactiveStageProp && interactiveStageProp !== stage);

  const canEditInitiationFields =
    !permissions.read_only_all && permissions.can_edit_initiation && workflowStage === 'initiation';
  const canEditActionFields =
    !permissions.read_only_all && permissions.can_edit_action && workflowStage === 'action';
  const canEditApprovalFields =
    !permissions.read_only_all &&
    (permissions.can_edit_approval_fields ?? permissions.can_approve) &&
    workflowStage === 'approval';
  const canEditClosingFields =
    !permissions.read_only_all &&
    (permissions.can_edit_closing_fields ?? permissions.can_close) &&
    workflowStage === 'closing';

  const openingDateReadOnly = !isAdmin();

  const lockInitiation = !canEditInitiationFields || screenMismatch('initiation');
  const lockAction = !canEditActionFields || screenMismatch('action');
  const lockApproval = !canEditApprovalFields || screenMismatch('approval');
  const lockClosing = !canEditClosingFields || screenMismatch('closing');

  const dueDateSet = Boolean(String(formData.due_date || '').trim());
  const lockActionUntilDueDate =
    workflowStage === 'action' && !dueDateSet;
  const lockActionFieldsExceptDueDate = lockAction || lockActionUntilDueDate;

  const textFilled = (v) => Boolean(v && String(v).trim());
  const actionSubmitReady =
    dueDateSet &&
    textFilled(formData.root_cause_description) &&
    (textFilled(formData.corrective_action_description) || textFilled(formData.corrective_action_description_attachment));

  const hotelOptions = useMemo(
    () => hotels.map((h) => ({ value: String(h.id), label: h.hotel_name || String(h.id) })),
    [hotels]
  );

  const filteredDepartments = useMemo(
    () => departments.filter((item) => !selectedHotelId || String(item.hotel_id) === String(selectedHotelId)),
    [departments, selectedHotelId]
  );

  const formatDepartmentLabel = (d) => {
    const name = d?.department_name || String(d?.id || '');
    const hotel = d?.hotel_name?.trim();
    return hotel ? `${name} (${hotel})` : name;
  };

  const departmentOptions = useMemo(
    () =>
      filteredDepartments.map((d) => ({
        value: String(d.id),
        label: formatDepartmentLabel(d),
      })),
    [filteredDepartments]
  );

  const formHotelScopeId = useMemo(() => {
    if (selectedHotelId) return String(selectedHotelId);
    for (const depId of [initialData?.df_initiator_department, initialData?.df_receiver_department]) {
      if (!depId) continue;
      const d = departments.find((x) => String(x.id) === String(depId));
      if (d?.hotel_id != null) return String(d.hotel_id);
    }
    return '';
  }, [
    selectedHotelId,
    initialData?.df_initiator_department,
    initialData?.df_receiver_department,
    departments,
  ]);

  const departmentsForFormHotel = useMemo(() => {
    if (!formHotelScopeId) return departments;
    return departments.filter((item) => String(item.hotel_id) === String(formHotelScopeId));
  }, [departments, formHotelScopeId]);

  const closingDepartmentOptions = useMemo(
    () =>
      departmentsForFormHotel.map((d) => ({
        value: String(d.id),
        label: formatDepartmentLabel(d),
      })),
    [departmentsForFormHotel]
  );

  const initiatorUsers = useMemo(() => {
    const dep = formData.df_initiator_department;
    if (!dep) return [];
    return qmUsers.filter((u) => String(u.department) === String(dep));
  }, [qmUsers, formData.df_initiator_department]);

  const receiverUsers = useMemo(() => {
    const dep = formData.df_receiver_department;
    if (!dep) return [];
    return qmUsers.filter((u) => String(u.department) === String(dep));
  }, [qmUsers, formData.df_receiver_department]);

  const initiatorUserOptions = useMemo(
    () => initiatorUsers.map((u) => ({ value: String(u.id), label: u.full_name || String(u.id) })),
    [initiatorUsers]
  );

  const receiverUserOptions = useMemo(
    () => receiverUsers.map((u) => ({ value: String(u.id), label: u.full_name || String(u.id) })),
    [receiverUsers]
  );

  const closingUserOptions = useMemo(
    () => closingUsersList.map((u) => ({ value: String(u.id), label: u.full_name || String(u.id) })),
    [closingUsersList]
  );

  useEffect(() => {
    if (!formData.closing_department) {
      setClosingUsersList([]);
      return;
    }
    let cancelled = false;
    setClosingUsersLoading(true);
    qualityManagementApi
      .getQmUsers(null, formData.closing_department)
      .then((res) => {
        if (!cancelled) {
          const rows = res?.data;
          setClosingUsersList(Array.isArray(rows) ? rows : []);
        }
      })
      .catch(() => {
        if (!cancelled) setClosingUsersList([]);
      })
      .finally(() => {
        if (!cancelled) setClosingUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.closing_department]);

  useEffect(() => {
    if (!closingUserId) return;
    const exists = closingUsersList.some((u) => String(u.id) === String(closingUserId));
    if (!exists) {
      setClosingUserId('');
      setFormData((prev) => ({ ...prev, closing_qm_user: '' }));
    }
  }, [closingUsersList, closingUserId]);

  const ncSummaryOptions = useMemo(
    () => nonconformitySummaries.map((item) => ({ value: item.summary_name, label: item.summary_name })),
    [nonconformitySummaries]
  );

  const ncSourceOptions = useMemo(
    () => nonconformitySources.map((item) => ({ value: item.source_name, label: item.source_name })),
    [nonconformitySources]
  );

  const rootCauseSummaryOptions = useMemo(() => {
    const base = rootCauseSummaries.map((item) => ({
      value: item.root_cause_name,
      label: item.root_cause_name,
    }));
    const v = String(formData.root_cause_summary || '').trim();
    if (v && !base.some((o) => o.value === v)) {
      return [{ value: v, label: `${v} (kayıtlı metin)` }, ...base];
    }
    return base;
  }, [rootCauseSummaries, formData.root_cause_summary]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset';
      return;
    }

    document.body.style.overflow = 'hidden';

    if (initialData) {
      setFormData({
        df_definition: initialData.df_definition || '',
        dep_code: initialData.dep_code || '',
        df_initiator_department: initialData.df_initiator_department || '',
        df_receiver_department: initialData.df_receiver_department || '',
        df_initiator_full_name: initialData.df_initiator_full_name || '',
        df_receiver_full_name: initialData.df_receiver_full_name || '',
        opening_date: initialData.opening_date || '',
        due_date: initialData.due_date || '',
        completion_date: initialData.completion_date || '',
        related_document: initialData.related_document || '',
        related_document_name: initialData.related_document_name || '',
        nonconformity_description: initialData.nonconformity_description || '',
        nonconformity_description_attachment: initialData.nonconformity_description_attachment || '',
        nonconformity_summary: initialData.nonconformity_summary || '',
        nonconformity_source: initialData.nonconformity_source || '',
        root_cause_description: initialData.root_cause_description || '',
        root_cause_summary: initialData.root_cause_summary || '',
        corrective_action_description: initialData.corrective_action_description || '',
        corrective_action_description_attachment: initialData.corrective_action_description_attachment || '',
        closing_notes: initialData.closing_notes || '',
        approver_full_name: initialData.approver_full_name || '',
        approval_decision: initialData.approval_decision || '',
        approval_comment: initialData.approval_comment || '',
        approval_confirmed: Boolean(initialData.approval_confirmed),
        closing_department: initialData.closing_department || '',
        closing_qm_user: initialData.closing_qm_user || '',
      });
      const initUser = qmUsers.find(
        (u) =>
          u.full_name === (initialData.df_initiator_full_name || '') &&
          String(u.department) === String(initialData.df_initiator_department || '')
      );
      const recvUser = qmUsers.find(
        (u) =>
          u.full_name === (initialData.df_receiver_full_name || '') &&
          String(u.department) === String(initialData.df_receiver_department || '')
      );
      setInitiatorUserId(initUser ? String(initUser.id) : '');
      setReceiverUserId(recvUser ? String(recvUser.id) : '');
      const initialHotelByDepartment = departments.find(
        (d) => String(d.id) === String(initialData.df_initiator_department || initialData.df_receiver_department || '')
      );
      setSelectedHotelId(initialHotelByDepartment ? String(initialHotelByDepartment.hotel_id) : '');
      setCounterWarning('');
      setRelatedDocumentFile(null);
      setRelatedDocumentLabel(initialData.related_document || '');
      setNcAttachmentFile(null);
      setNcAttachmentLabel(initialData.nonconformity_description_attachment || '');
      setCaAttachmentFile(null);
      setCaAttachmentLabel(initialData.corrective_action_description_attachment || '');
      setAutoDfNo(initialData.df_definition_no || '');
      const cUser = qmUsers.find((u) => String(u.id) === String(initialData.closing_qm_user || ''));
      setClosingUserId(cUser ? String(cUser.id) : '');
    } else {
      setFormData({
        ...initialFormState,
        opening_date: new Date().toISOString().slice(0, 10),
      });
      setAutoDfNo('');
      setSelectedHotelId('');
      setInitiatorUserId('');
      setReceiverUserId('');
      setCounterWarning('');
      setRelatedDocumentFile(null);
      setRelatedDocumentLabel('');
      setNcAttachmentFile(null);
      setNcAttachmentLabel('');
      setCaAttachmentFile(null);
      setCaAttachmentLabel('');
      setClosingUserId('');
    }
    setError('');
    setOpenStage('stage1');

    if (!initialData) setAutoDfNo('');

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialData, qmUsers, departments]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRelatedDocumentPick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setRelatedDocumentFile(file);
    setRelatedDocumentLabel(file.name);
    handleInputChange('related_document', file.name);
  };

  const handleNcAttachmentPick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setNcAttachmentFile(file);
    setNcAttachmentLabel(file.name);
    handleInputChange('nonconformity_description_attachment', file.name);
  };

  const handleCaAttachmentPick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCaAttachmentFile(file);
    setCaAttachmentLabel(file.name);
    handleInputChange('corrective_action_description_attachment', file.name);
  };

  useEffect(() => {
    if (!isOpen || initialData) return;

    if (!selectedHotelId || !formData.df_initiator_department) {
      setAutoDfNo('');
      setCounterWarning('');
      return;
    }

    const selectedDepartment = departments.find(
      (item) => String(item.id) === String(formData.df_initiator_department)
    );
    if (!selectedDepartment || String(selectedDepartment.hotel_id) !== String(selectedHotelId)) {
      setAutoDfNo('');
      setCounterWarning('');
      return;
    }

    qualityManagementApi
      .getNextDfNumber(formData.df_initiator_department, selectedHotelId)
      .then((res) => {
        const nextNo = res?.next_df_no || '';
        setAutoDfNo(nextNo);
        setCounterWarning(nextNo ? '' : 'Sayac tanimli degil');
      })
      .catch(() => {
        setAutoDfNo('');
        setCounterWarning('Sayac tanimli degil');
      });
  }, [isOpen, initialData, formData.df_initiator_department, selectedHotelId, departments]);

  useEffect(() => {
    if (!formData.df_initiator_department) return;
    const selected = departments.find((item) => String(item.id) === String(formData.df_initiator_department));
    if (selected?.dep_code) {
      setFormData((prev) => ({ ...prev, dep_code: selected.dep_code }));
      if (selected.hotel_id && String(selectedHotelId) !== String(selected.hotel_id)) {
        setSelectedHotelId(String(selected.hotel_id));
      }
    } else {
      setFormData((prev) => ({ ...prev, dep_code: '' }));
    }
  }, [formData.df_initiator_department, departments, selectedHotelId]);

  useEffect(() => {
    if (!initiatorUserId) return;
    const exists = initiatorUsers.some((u) => String(u.id) === String(initiatorUserId));
    if (!exists) {
      setInitiatorUserId('');
      handleInputChange('df_initiator_full_name', '');
    }
  }, [initiatorUsers, initiatorUserId]);

  useEffect(() => {
    if (!receiverUserId) return;
    const exists = receiverUsers.some((u) => String(u.id) === String(receiverUserId));
    if (!exists) {
      setReceiverUserId('');
      handleInputChange('df_receiver_full_name', '');
    }
  }, [receiverUsers, receiverUserId]);

  const handleInitiatorUserChange = (userId) => {
    setInitiatorUserId(userId || '');
    if (!userId) {
      handleInputChange('df_initiator_full_name', '');
      return;
    }
    const selected = initiatorUsers.find((u) => String(u.id) === String(userId));
    if (!selected) {
      handleInputChange('df_initiator_full_name', '');
      return;
    }
    if (selected.hotel_id && String(selectedHotelId) !== String(selected.hotel_id)) {
      setSelectedHotelId(String(selected.hotel_id));
    }
    handleInputChange('df_initiator_department', String(selected.department));
    handleInputChange('df_initiator_full_name', selected.full_name);
  };

  const handleReceiverUserChange = (userId) => {
    setReceiverUserId(userId || '');
    if (!userId) {
      handleInputChange('df_receiver_full_name', '');
      return;
    }
    const selected = receiverUsers.find((u) => String(u.id) === String(userId));
    if (!selected) {
      handleInputChange('df_receiver_full_name', '');
      return;
    }
    handleInputChange('df_receiver_department', String(selected.department));
    handleInputChange('df_receiver_full_name', selected.full_name);
  };

  const handleClosingUserChange = (userId) => {
    setClosingUserId(userId || '');
    if (!userId) {
      handleInputChange('closing_qm_user', '');
      return;
    }
    const selected = closingUsersList.find((u) => String(u.id) === String(userId));
    if (!selected) {
      handleInputChange('closing_qm_user', '');
      return;
    }
    handleInputChange('closing_qm_user', String(selected.id));
  };

  const handleHotelChange = (hotelId) => {
    setSelectedHotelId(hotelId);
    if (!hotelId) {
      handleInputChange('df_initiator_department', '');
      handleInputChange('df_receiver_department', '');
      handleInputChange('dep_code', '');
      return;
    }
    const nextDepartments = departments.filter((d) => String(d.hotel_id) === String(hotelId));
    const initiatorValid = nextDepartments.some((d) => String(d.id) === String(formData.df_initiator_department));
    const receiverValid = nextDepartments.some((d) => String(d.id) === String(formData.df_receiver_department));
    if (!initiatorValid) {
      handleInputChange('df_initiator_department', '');
      handleInputChange('df_initiator_full_name', '');
      setInitiatorUserId('');
      handleInputChange('dep_code', '');
    }
    if (!receiverValid) {
      handleInputChange('df_receiver_department', '');
      handleInputChange('df_receiver_full_name', '');
      setReceiverUserId('');
    }
  };

  const renderStageHeader = (stageKey, title, subtitle) => {
    const expanded = openStage === stageKey;
    return (
      <button
        type="button"
        onClick={() => setOpenStage(expanded ? '' : stageKey)}
        className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors ${
          expanded
            ? 'border-blue-200 bg-blue-50/60 dark:border-blue-800/50 dark:bg-blue-950/20'
            : 'border-transparent bg-transparent hover:bg-slate-50 dark:hover:bg-gray-800/40'
        }`}
      >
        <div className="flex min-w-0 items-baseline gap-2.5">
          <span className={`text-sm font-medium ${expanded ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-gray-300'}`}>
            {title}
          </span>
          {subtitle && (
            <span className="hidden truncate text-[11px] text-slate-400 dark:text-gray-500 sm:inline">
              {subtitle}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-400 dark:text-gray-500" />
        )}
      </button>
    );
  };

  /** Kamera / bazı mobil tarayıcılarda File.name boş olabilir; FormData multipart'ta uzantılı ad şart */
  const attachmentFileAppendName = (file, fallbackStem = 'attachment') => {
    if (!(file instanceof Blob)) {
      return `${fallbackStem}.bin`;
    }
    const raw = typeof file.name === 'string' ? file.name.trim() : '';
    if (raw) return raw;
    if (typeof file.type === 'string' && file.type.startsWith('image/')) {
      return `${fallbackStem}.jpg`;
    }
    if (typeof file.type === 'string' && file.type.includes('pdf')) {
      return `${fallbackStem}.pdf`;
    }
    return `${fallbackStem}.bin`;
  };

  const appendFormPayload = (flags = {}) => {
    const rawPayload = {
      ...formData,
      preview_area: '',
      df_no: autoDfNo,
      df_definition: formData.df_definition ? Number(formData.df_definition) : null,
      df_initiator_department: formData.df_initiator_department ? Number(formData.df_initiator_department) : null,
      df_receiver_department: formData.df_receiver_department ? Number(formData.df_receiver_department) : null,
      closing_department: formData.closing_department ? Number(formData.closing_department) : null,
      closing_qm_user: formData.closing_qm_user ? Number(formData.closing_qm_user) : null,
    };
    if (workflowStage === 'approval') {
      rawPayload.approval_confirmed = true;
    }
    const payload = new FormData();
    Object.entries(rawPayload).forEach(([key, value]) => {
      if (key === 'approval_confirmed') {
        payload.append(key, value === true || value === 'true' ? 'true' : 'false');
        return;
      }
      payload.append(key, value ?? '');
    });
    if (relatedDocumentFile) {
      payload.append(
        'related_document_file',
        relatedDocumentFile,
        attachmentFileAppendName(relatedDocumentFile, 'ilgili-doc')
      );
    }
    if (ncAttachmentFile) {
      payload.append(
        'nonconformity_attachment_file',
        ncAttachmentFile,
        attachmentFileAppendName(ncAttachmentFile, 'tanim-eki')
      );
    }
    if (caAttachmentFile) {
      payload.append(
        'corrective_action_attachment_file',
        caAttachmentFile,
        attachmentFileAppendName(caAttachmentFile, 'faaliyet-ek')
      );
    }
    if (flags.submit_initiation) {
      payload.append('submit_initiation', 'true');
    }
    if (flags.submit_action) {
      payload.append('submit_action', 'true');
    }
    return payload;
  };

  const handleSubmit = async (e, flags = {}) => {
    e.preventDefault();
    setError('');
    if (workflowStage === 'action' && flags.submit_action) {
      if (!dueDateSet) {
        setError('Önce termin tarihini girip kaydedin; ardından diğer zorunlu alanları doldurup onaya gönderebilirsiniz.');
        return;
      }
      if (!textFilled(formData.root_cause_description)) {
        setError('Onaya göndermek için kök neden açıklaması zorunludur.');
        return;
      }
      if (
        !textFilled(formData.corrective_action_description) &&
        !textFilled(formData.corrective_action_description_attachment)
      ) {
        setError(
          'Onaya göndermek için düzeltici faaliyet tanımı veya dosya / bağlantı alanlarından en az biri dolu olmalıdır.'
        );
        return;
      }
    }
    if (workflowStage === 'approval' || workflowStage === 'closing') {
      const payload = appendFormPayload(flags);
      try {
        await onSave(payload);
      } catch (err) {
        setError(err.message || 'Kaydedilemedi.');
      }
      return;
    }
    if (!formData.df_initiator_department) {
      setError('Baslatan departman secimi zorunludur.');
      return;
    }
    if (!selectedHotelId) {
      setError('Otel secimi zorunludur.');
      return;
    }
    if (!autoDfNo) {
      setError('Duzeltici Faaliyet numarasi olusturulamadi.');
      return;
    }

    if (flags.submit_initiation) {
      if (!textFilled(formData.nonconformity_description)) {
        setError('Aksiyon aşamasına geçmek için uygunsuzluk açıklaması zorunludur.');
        return;
      }
      if (!String(formData.nonconformity_source || '').trim()) {
        setError('Aksiyon aşamasına geçmek için uygunsuzluk kaynağı seçilmelidir.');
        return;
      }
    }

    const payload = appendFormPayload(flags);
    try {
      await onSave(payload);
    } catch (err) {
      setError(err.message || 'Kaydedilemedi.');
    }
  };

  const handleCloseDecision = async (closed) => {
    if (!initialData?.id) return;
    setTransitionLoading(true);
    setError('');
    try {
      await qualityManagementApi.closeForm(initialData.id, closed);
      onDone?.();
    } catch (err) {
      setError(err.message || 'Kapatma kaydedilemedi.');
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleSendToClosing = async (overrides = {}) => {
    if (!initialData?.id) return;
    setTransitionLoading(true);
    setError('');
    try {
      await qualityManagementApi.sendFormToClosing(initialData.id, {
        approval_decision: (overrides.approval_decision ?? formData.approval_decision) || '',
        approval_confirmed: true,
        approval_comment: overrides.approval_comment ?? formData.approval_comment ?? '',
        approver_full_name: overrides.approver_full_name ?? formData.approver_full_name ?? '',
      });
      onDone?.();
    } catch (err) {
      setError(err.message || 'Kapatmaya gonderilemedi.');
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleApproveToClosing = async () => {
    await handleSendToClosing({
      approval_decision: 'suitable',
      approval_confirmed: true,
    });
  };

  const handleRejectAndClone = async () => {
    if (!initialData?.id) return;
    setTransitionLoading(true);
    setError('');
    try {
      await qualityManagementApi.rejectAndCloneForm(initialData.id, {
        approval_comment: formData.approval_comment || '',
      });
      onDone?.();
    } catch (err) {
      setError(err.message || 'Red ve klon islemi basarisiz.');
    } finally {
      setTransitionLoading(false);
    }
  };

  const selectedHotelOption = hotelOptions.find((o) => o.value === String(selectedHotelId)) || null;
  const selectedInitiatorDeptOption =
    departmentOptions.find((o) => o.value === String(formData.df_initiator_department)) || null;
  const selectedReceiverDeptOption =
    departmentOptions.find((o) => o.value === String(formData.df_receiver_department)) || null;
  const selectedInitiatorUserOption =
    initiatorUserOptions.find((o) => o.value === String(initiatorUserId)) || null;
  const selectedReceiverUserOption =
    receiverUserOptions.find((o) => o.value === String(receiverUserId)) || null;
  const selectedNcSummary =
    ncSummaryOptions.find((o) => o.value === formData.nonconformity_summary) || null;
  const selectedNcSource = ncSourceOptions.find((o) => o.value === formData.nonconformity_source) || null;
  const selectedRootCauseSummary =
    rootCauseSummaryOptions.find((o) => o.value === formData.root_cause_summary) || null;
  const selectedClosingDeptOption =
    closingDepartmentOptions.find((o) => o.value === String(formData.closing_department)) || null;
  const selectedClosingUserOption =
    closingUserOptions.find((o) => o.value === String(closingUserId)) || null;

  const selectCommonProps = {
    styles: qmSelectStyles,
    classNames: qmSelectClassNames,
    classNamePrefix: 'qm-rs',
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    isSearchable: true,
    isClearable: true,
  };

  /** Tüm aşamalarda aynı görünen Kaydet düğmesi */
  const qmSaveButtonClassName =
    'px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:cursor-not-allowed';

  const initiationCompleteDisabled =
    transitionLoading || lockInitiation || !permissions.can_submit_initiation;

  const initiationCompleteTitle = (() => {
    if (transitionLoading) return '';
    if (lockInitiation && screenMismatch('initiation')) {
      return 'Başlatmayı tamamlamak için DOIF Başlat menüsünden bu kaydı açın.';
    }
    if (lockInitiation) {
      return 'Başlatma alanları sizin için salt okunur.';
    }
    if (!permissions.can_submit_initiation) {
      return 'Bu adımı yalnızca başlatan departmandaki Kalite kullanıcısı veya Admin/IT tamamlayabilir.';
    }
    return 'Uygunsuzluk açıklaması ve kaynağı doldurduktan sonra aksiyon aşamasına geçilir.';
  })();

  const dfNoDisplay = useMemo(
    () => String(initialData?.df_definition_no || autoDfNo || '').trim(),
    [initialData?.df_definition_no, autoDfNo]
  );

  const workflowProgressPercent = useMemo(() => {
    const w = workflowStage;
    if (w === 'completed') return 100;
    if (w === 'rejected') return 100;
    if (w === 'closing') return 90;
    if (w === 'approval') return 70;
    if (w === 'action') return 45;
    if (w === 'initiation') return 25;
    return 15;
  }, [workflowStage]);

  const stepSidebarItems = useMemo(
    () => [
      { key: 'stage1', label: 'Başlatma', short: '1' },
      { key: 'stage2', label: 'Aksiyon', short: '2' },
      { key: 'stage3', label: 'Onay', short: '3' },
      { key: 'stage4', label: 'Kapatma', short: '4' },
    ],
    []
  );

  const isWorkflowStepDone = (idx) => {
    const w = workflowStage;
    if (w === 'completed' || w === 'rejected') return true;
    if (idx === 0) return w !== 'initiation';
    if (idx === 1) return ['approval', 'closing', 'completed'].includes(w) || w === 'rejected';
    if (idx === 2) return ['closing', 'completed'].includes(w) || w === 'rejected';
    if (idx === 3) return w === 'completed' || w === 'rejected';
    return false;
  };

  const summaryHotelLabel = useMemo(() => {
    if (selectedHotelOption?.label) {
      return String(selectedHotelOption.label).replace(/\s*\([^)]+\)\s*$/, '').trim();
    }
    return '—';
  }, [selectedHotelOption]);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      className="!max-w-[min(100vw-1.25rem,92rem)] w-full"
    >
      <div className="flex h-[min(92dvh,calc(100vh-4rem))] w-full min-h-0 flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-900">
      <div className="flex shrink-0 min-w-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
          <h2 className="min-w-0 text-base font-bold text-slate-900 dark:text-gray-50 sm:text-lg">
            Düzeltici Faaliyet Formu
          </h2>
          <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:border-blue-800/80 dark:bg-blue-950/60 dark:text-blue-300">
            {WORKFLOW_STAGE_LABELS[workflowStage] || workflowStage}
          </span>
          {dfNoDisplay && (
            <span className="font-mono text-sm text-slate-500 tabular-nums dark:text-gray-400">
              {dfNoDisplay}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          aria-label="Kapat"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <form
        onSubmit={(e) => handleSubmit(e, {})}
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden bg-white dark:bg-gray-900"
      >
        <div className="shrink-0 border-b border-slate-200 bg-slate-50/90 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800/90 lg:hidden">
          <div className="mb-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
            <span>İlerleme</span>
            <span className="text-blue-600 tabular-nums dark:text-blue-400">{workflowProgressPercent}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-blue-600 transition-[width] dark:bg-blue-500"
              style={{ width: `${Math.min(100, workflowProgressPercent)}%` }}
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <div className="min-h-0 flex-1 space-y-3 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5 lg:min-w-0 lg:pr-2">
        <input
          ref={documentInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={handleRelatedDocumentPick}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleRelatedDocumentPick}
        />
        <input
          ref={ncDocumentInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={handleNcAttachmentPick}
        />
        <input
          ref={ncCameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleNcAttachmentPick}
        />
        <input
          ref={caDocumentInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={handleCaAttachmentPick}
        />
        <input
          ref={caCameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleCaAttachmentPick}
        />
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-2 text-sm">
            {error}
          </div>
        )}

        {workflowStage === 'initiation' && screenMismatch('initiation') && initialData?.id && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/90 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 px-4 py-2 text-xs leading-relaxed">
            Başlatma alanları bu menüde kilitli. &quot;Başlatmayı tamamla&quot; ve düzenleme için{' '}
            <span className="font-semibold">DOIF Başlat</span> sayfasından bu kaydı açın.
          </div>
        )}

        <div className="space-y-1.5">
          {renderStageHeader('stage1', 'Aşama 1 — Başlatma', 'Otel, departman ve uygunsuzluk bilgileri')}
          {openStage === 'stage1' && (
            <div className="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
              <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <QmFieldset legend="Otel" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder="Seciniz"
                    isDisabled={lockInitiation}
                    options={hotelOptions}
                    value={selectedHotelOption}
                    onChange={(opt) => handleHotelChange(opt?.value || '')}
                  />
                </QmFieldset>
                <QmFieldset legend="Açılış Tarihi" singleLine>
                  <input
                    type="date"
                    value={formData.opening_date || ''}
                    onChange={(e) => handleInputChange('opening_date', e.target.value)}
                    disabled={lockInitiation || openingDateReadOnly}
                    tabIndex={lockInitiation || openingDateReadOnly ? -1 : 0}
                    className={`${qmSingleLineInputClass} w-full ${
                      lockInitiation || openingDateReadOnly ? 'opacity-80 cursor-default' : ''
                    }`}
                  />
                </QmFieldset>
                <QmFieldset legend="Başlatan Departman" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder="Seciniz"
                    isDisabled={lockInitiation}
                    options={departmentOptions}
                    value={selectedInitiatorDeptOption}
                    onChange={(opt) => handleInputChange('df_initiator_department', opt?.value || '')}
                  />
                </QmFieldset>
                <QmFieldset legend="DF No / Dep. Kodu" singleLine>
                  <p
                    className="w-full truncate text-xs font-medium tabular-nums text-gray-900 dark:text-gray-100 cursor-default select-text"
                    title={`${autoDfNo || '—'} · Kod: ${formData.dep_code || '—'}`}
                  >
                    {autoDfNo || '—'}
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                      {' '}
                      · Kod: {formData.dep_code || '—'}
                    </span>
                  </p>
                </QmFieldset>
              </div>
              {counterWarning && (
                <p className="text-xs text-red-600 dark:text-red-400">{counterWarning}</p>
              )}

              <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-3">
                <QmFieldset legend="Başlatan Kişi" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder={formData.df_initiator_department ? 'Seciniz' : 'Once departman seciniz'}
                    isDisabled={lockInitiation || !formData.df_initiator_department}
                    options={initiatorUserOptions}
                    value={selectedInitiatorUserOption}
                    onChange={(opt) => handleInitiatorUserChange(opt?.value || '')}
                  />
                </QmFieldset>
                <QmFieldset legend="Faaliyeti Alan Departman" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder="Seciniz"
                    isDisabled={lockInitiation}
                    options={departmentOptions}
                    value={selectedReceiverDeptOption}
                    onChange={(opt) => handleInputChange('df_receiver_department', opt?.value || '')}
                  />
                </QmFieldset>
                <QmFieldset legend="Faaliyeti Alan Kişi" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder={formData.df_receiver_department ? 'Seciniz' : 'Once departman seciniz'}
                    isDisabled={lockInitiation || !formData.df_receiver_department}
                    options={receiverUserOptions}
                    value={selectedReceiverUserOption}
                    onChange={(opt) => handleReceiverUserChange(opt?.value || '')}
                  />
                </QmFieldset>
              </div>

              <div className="space-y-3">
                <p className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  İlgili Doküman / Referans
                </p>
                <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
                  <QmFieldset legend="Belge Adı" className="min-w-0" singleLine>
                    <input
                      type="text"
                      value={formData.related_document_name}
                      onChange={(e) => handleInputChange('related_document_name', e.target.value)}
                      disabled={lockInitiation}
                      className={`${qmSingleLineInputClass} w-full`}
                    />
                  </QmFieldset>
                  <QmFieldset legend="Yüklenen Dosya" className="min-w-0">
                    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
                      <input
                        value={relatedDocumentLabel || formData.related_document || ''}
                        readOnly
                        tabIndex={-1}
                        className={`${qmSingleLineInputClass} w-full min-w-0 cursor-default text-gray-700 dark:text-gray-300 sm:flex-1`}
                      />
                      <div className="flex shrink-0 flex-wrap justify-end gap-2 sm:flex-nowrap sm:justify-start">
                        <button
                          type="button"
                          onClick={() => documentInputRef.current?.click()}
                          disabled={lockInitiation}
                          title="Dosya ekle"
                          className={`${qmFileAttachBtnClass} bg-gray-700 hover:bg-gray-600`}
                        >
                          <PaperClipIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                          <span>Dosya</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => cameraInputRef.current?.click()}
                          disabled={lockInitiation}
                          title="Kameradan cek"
                          className={`${qmFileAttachBtnClass} bg-highlight-600 hover:bg-highlight-500`}
                        >
                          <CameraIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                          <span>Kamera</span>
                        </button>
                      </div>
                    </div>
                  </QmFieldset>
                </div>
              </div>

              <QmFieldset legend="Uygunsuzluk Tanımı">
                <textarea
                  value={formData.nonconformity_description}
                  onChange={(e) => handleInputChange('nonconformity_description', e.target.value)}
                  disabled={lockInitiation}
                  rows={2}
                  className={`${qmFieldsetNativeClass} resize-y min-h-[52px]`}
                />
              </QmFieldset>

              <div className="space-y-3">
                <p className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Özet, kaynak ve tanım eki
                </p>
                <div className="grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
                  <QmFieldset legend="Uygunsuzluk Özeti" className="min-w-0" singleLine>
                    <Select
                      {...selectCommonProps}
                      className="w-full"
                      placeholder="Seciniz"
                      isDisabled={lockInitiation}
                      options={ncSummaryOptions}
                      value={selectedNcSummary}
                      onChange={(opt) => handleInputChange('nonconformity_summary', opt?.value || '')}
                    />
                  </QmFieldset>
                  <QmFieldset legend="Uygunsuzluk Kaynağı" className="min-w-0" singleLine>
                    <Select
                      {...selectCommonProps}
                      className="w-full"
                      placeholder="Seciniz"
                      isDisabled={lockInitiation}
                      options={ncSourceOptions}
                      value={selectedNcSource}
                      onChange={(opt) => handleInputChange('nonconformity_source', opt?.value || '')}
                    />
                  </QmFieldset>
                </div>
                <QmFieldset legend="Tanım Eki" className="min-w-0">
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
                    <input
                      value={ncAttachmentLabel || formData.nonconformity_description_attachment || ''}
                      readOnly
                      tabIndex={-1}
                      title={ncAttachmentLabel || formData.nonconformity_description_attachment || ''}
                      className={`${qmSingleLineInputClass} w-full min-w-0 cursor-default text-gray-700 dark:text-gray-300 sm:flex-1`}
                    />
                    <div className="flex shrink-0 flex-wrap justify-end gap-2 sm:flex-nowrap sm:justify-start">
                      <button
                        type="button"
                        onClick={() => ncDocumentInputRef.current?.click()}
                        disabled={lockInitiation}
                        title="Dosya ekle"
                        className={`${qmFileAttachBtnClass} bg-gray-700 hover:bg-gray-600`}
                      >
                        <PaperClipIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                        <span>Dosya</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => ncCameraInputRef.current?.click()}
                        disabled={lockInitiation}
                        title="Kameradan cek"
                        className={`${qmFileAttachBtnClass} bg-highlight-600 hover:bg-highlight-500`}
                      >
                        <CameraIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                        <span>Kamera</span>
                      </button>
                    </div>
                  </div>
                </QmFieldset>
              </div>
            </div>
          )}

          {renderStageHeader(
            'stage2',
            'Aşama 2 — Aksiyon',
            'Önce termin tarihini girin ve kaydedin; ardından kök neden ile düzeltici faaliyet tanımı veya dosya/bağlantıyı doldurun.'
          )}
          {openStage === 'stage2' && (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700 sm:p-5">
              <QmFieldset legend="Termin Tarihi" className="min-w-0" singleLine>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                  disabled={lockAction}
                  className={`${qmSingleLineInputClass} w-full`}
                />
              </QmFieldset>
              {workflowStage === 'action' && !lockAction && lockActionUntilDueDate && (
                <p className="text-xs text-amber-700 dark:text-amber-300 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                  Diğer alanları doldurmadan önce termin tarihini seçip <strong>Kaydet</strong> ile kaydedin.
                </p>
              )}
              <QmFieldset legend="Kök Neden">
                <textarea
                  value={formData.root_cause_description}
                  onChange={(e) => handleInputChange('root_cause_description', e.target.value)}
                  disabled={lockActionFieldsExceptDueDate}
                  rows={3}
                  className={`${qmFieldsetNativeClass} resize-y min-h-[72px]`}
                />
              </QmFieldset>
              <QmFieldset legend="Kök Neden Özeti" className="min-w-0" singleLine>
                <Select
                  {...selectCommonProps}
                  className="w-full"
                  placeholder="Seciniz"
                  isDisabled={lockActionFieldsExceptDueDate}
                  options={rootCauseSummaryOptions}
                  value={selectedRootCauseSummary}
                  onChange={(opt) => handleInputChange('root_cause_summary', opt?.value || '')}
                />
              </QmFieldset>
              <div className="space-y-3">
                <p className="text-[11px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Düzeltici Faaliyet Detayı
                </p>
                <QmFieldset legend="Düzeltici Faaliyet Tanımı">
                  <textarea
                    value={formData.corrective_action_description}
                    onChange={(e) => handleInputChange('corrective_action_description', e.target.value)}
                    disabled={lockActionFieldsExceptDueDate}
                    rows={2}
                    className={`${qmFieldsetNativeClass} resize-y min-h-[52px]`}
                  />
                </QmFieldset>
                <QmFieldset legend="Faaliyet Eki" className="min-w-0">
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch">
                    <input
                      type="text"
                      value={
                        caAttachmentFile ? caAttachmentLabel : formData.corrective_action_description_attachment || ''
                      }
                      onChange={(e) => {
                        setCaAttachmentFile(null);
                        handleInputChange('corrective_action_description_attachment', e.target.value);
                      }}
                      readOnly={Boolean(caAttachmentFile) || lockActionFieldsExceptDueDate}
                      disabled={lockActionFieldsExceptDueDate}
                      placeholder="https://... veya Dosya / Kamera ile ekleyin"
                      title={formData.corrective_action_description_attachment || ''}
                      className={`${qmSingleLineInputClass} w-full min-w-0 text-gray-900 dark:text-gray-100 sm:flex-1`}
                    />
                    <div className="flex shrink-0 flex-wrap justify-end gap-2 sm:flex-nowrap sm:justify-start">
                      <button
                        type="button"
                        onClick={() => caDocumentInputRef.current?.click()}
                        disabled={lockActionFieldsExceptDueDate}
                        title="Dosya ekle"
                        className={`${qmFileAttachBtnClass} bg-gray-700 hover:bg-gray-600`}
                      >
                        <PaperClipIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                        <span>Dosya</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => caCameraInputRef.current?.click()}
                        disabled={lockActionFieldsExceptDueDate}
                        title="Kameradan cek"
                        className={`${qmFileAttachBtnClass} bg-highlight-600 hover:bg-highlight-500`}
                      >
                        <CameraIcon className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                        <span>Kamera</span>
                      </button>
                      {caAttachmentFile && !lockActionFieldsExceptDueDate && (
                        <button
                          type="button"
                          onClick={() => {
                            setCaAttachmentFile(null);
                            setCaAttachmentLabel('');
                            handleInputChange('corrective_action_description_attachment', '');
                          }}
                          className="inline-flex h-8 min-h-8 shrink-0 items-center justify-center rounded-md border border-gray-400 px-2 text-[10px] font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700 sm:h-9 sm:text-xs"
                        >
                          Temizle
                        </button>
                      )}
                    </div>
                  </div>
                </QmFieldset>
                <QmFieldset legend="Tamamlanma Tarihi" singleLine>
                  <input
                    type="date"
                    value={formData.completion_date}
                    readOnly
                    tabIndex={-1}
                    title={
                      workflowStage === 'action' && !formData.completion_date
                        ? 'Onaya Gönder dediğiniz günün tarihi otomatik kaydedilir'
                        : undefined
                    }
                    className={`${qmSingleLineInputClass} w-full cursor-default opacity-90`}
                  />
                </QmFieldset>
              </div>
            </div>
          )}

          {renderStageHeader('stage3', 'Aşama 3 — Onay', 'Karar ve açıklama')}
          {openStage === 'stage3' && (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700 sm:p-5">
              {workflowStage === 'approval' && permissions.can_approve && (
                <div className="flex flex-wrap gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    disabled={transitionLoading || lockApproval}
                    onClick={handleApproveToClosing}
                    className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
                  >
                    Onayla (Kapatmaya gönder)
                  </button>
                  <button
                    type="button"
                    disabled={transitionLoading || lockApproval}
                    onClick={handleRejectAndClone}
                    className="px-4 py-2 rounded-md border border-red-500 text-red-800 dark:text-red-200 text-sm font-medium disabled:opacity-50"
                  >
                    Reddet (yeni DÖİF)
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <QmFieldset legend="DÖİF Başlatan Departman" singleLine>
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={
                      initialData?.df_initiator_department_name ||
                      departments.find((d) => String(d.id) === String(formData.df_initiator_department))
                        ?.department_name ||
                      '—'
                    }
                    className={`${qmSingleLineInputClass} w-full cursor-default text-gray-700 dark:text-gray-300`}
                  />
                </QmFieldset>
                <QmFieldset legend="DÖİF Başlatan Kişi" singleLine>
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={formData.df_initiator_full_name || initialData?.df_initiator_full_name || '—'}
                    className={`${qmSingleLineInputClass} w-full cursor-default text-gray-700 dark:text-gray-300`}
                  />
                </QmFieldset>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <QmFieldset legend="Onay Kararı" className="min-w-0" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder="Seciniz"
                    isDisabled={lockApproval}
                    options={[
                      { value: 'suitable', label: 'Uygundur' },
                      { value: 'rejected', label: 'Red' },
                    ]}
                    value={
                      formData.approval_decision
                        ? {
                            value: formData.approval_decision,
                            label: formData.approval_decision === 'suitable' ? 'Uygundur' : 'Red',
                          }
                        : null
                    }
                    onChange={(opt) => handleInputChange('approval_decision', opt?.value || '')}
                  />
                </QmFieldset>
                <QmFieldset legend="Onaylayan Ad Soyad" className="min-w-0" singleLine>
                  <input
                    type="text"
                    value={formData.approver_full_name}
                    onChange={(e) => handleInputChange('approver_full_name', e.target.value)}
                    disabled={lockApproval}
                    className={`${qmSingleLineInputClass} w-full`}
                  />
                </QmFieldset>
              </div>
              <QmFieldset legend="Onay Açıklaması">
                <textarea
                  value={formData.approval_comment}
                  onChange={(e) => handleInputChange('approval_comment', e.target.value)}
                  disabled={lockApproval}
                  rows={2}
                  className={`${qmFieldsetNativeClass} resize-y min-h-[52px]`}
                />
              </QmFieldset>
            </div>
          )}

          {renderStageHeader('stage4', 'Aşama 4 — Kapatma', 'Kapatma departmanı ve not')}
          {openStage === 'stage4' && (
            <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700 sm:p-5">
              <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
                <QmFieldset legend="DÖİF Kapatan Departman" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder="Seciniz"
                    isDisabled={lockClosing}
                    options={closingDepartmentOptions}
                    value={selectedClosingDeptOption}
                    onChange={(opt) => {
                      handleInputChange('closing_department', opt?.value || '');
                      setClosingUserId('');
                      handleInputChange('closing_qm_user', '');
                    }}
                  />
                </QmFieldset>
                <QmFieldset legend="DÖİF Kapatan Kişi" singleLine>
                  <Select
                    {...selectCommonProps}
                    className="w-full"
                    placeholder={
                      !formData.closing_department
                        ? 'Once departman seciniz'
                        : closingUsersLoading
                          ? 'Yukleniyor...'
                          : 'Seciniz'
                    }
                    isDisabled={lockClosing || !formData.closing_department}
                    isLoading={closingUsersLoading}
                    options={closingUserOptions}
                    value={selectedClosingUserOption}
                    onChange={(opt) => handleClosingUserChange(opt?.value || '')}
                  />
                </QmFieldset>
              </div>
              {formData.closing_department &&
                !closingUsersLoading &&
                closingUsersList.length === 0 && (
                  <p className="text-[10px] text-amber-700 dark:text-amber-300">
                    Bu departman icin kalite (QM) kullanicisi yok. Kalite Yonetimi &gt; Tanımlar &gt; Kullanicilar
                    ekraninda bu departmana kullanici tanimlayin.
                  </p>
                )}
              <QmFieldset legend="Kapatma Notu">
                <textarea
                  value={formData.closing_notes}
                  onChange={(e) => handleInputChange('closing_notes', e.target.value)}
                  disabled={lockClosing}
                  rows={3}
                  className={`${qmFieldsetNativeClass} resize-y min-h-[72px]`}
                />
              </QmFieldset>
            </div>
          )}
        </div>
        </div>

        <aside className="hidden w-64 shrink-0 flex-col overflow-y-auto border-l border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:flex">
          {/* Panel başlığı */}
          <div className="border-b border-slate-200 px-4 py-3 dark:border-gray-700">
            <p className="text-sm font-bold text-slate-900 dark:text-gray-100">ÖZET</p>
          </div>

          {/* İlerleme */}
          <div className="border-b border-slate-200 px-4 py-3 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-gray-400">İlerleme</p>
              <span className="text-[10px] font-bold tabular-nums text-slate-700 dark:text-gray-300">{workflowProgressPercent}%</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-blue-600 transition-[width] dark:bg-blue-500"
                style={{ width: `${Math.min(100, workflowProgressPercent)}%` }}
              />
            </div>
          </div>

          {/* Aşama listesi */}
          <div className="border-b border-slate-200 px-4 py-3 dark:border-gray-700">
            <ol className="space-y-2">
              {stepSidebarItems.map((item, idx) => {
                const done = isWorkflowStepDone(idx);
                const openHere = openStage === item.key;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => setOpenStage(item.key)}
                      className={`flex w-full items-center gap-2.5 rounded px-0 py-0.5 text-left text-xs transition-colors hover:opacity-80 ${
                        openHere ? 'opacity-100' : 'opacity-70 hover:opacity-90'
                      }`}
                    >
                      {done ? (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                        </span>
                      ) : (
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                          openHere
                            ? 'bg-blue-600 text-white'
                            : 'border border-slate-300 text-slate-500 dark:border-gray-600 dark:text-gray-400'
                        }`}>
                          {item.short}
                        </span>
                      )}
                      <span className={`min-w-0 font-medium ${
                        openHere
                          ? 'text-slate-900 dark:text-gray-50'
                          : done
                            ? 'text-slate-700 dark:text-gray-300'
                            : 'text-slate-500 dark:text-gray-500'
                      }`}>
                        {item.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Özet alanlar */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <dl className="space-y-3 text-xs">
              {[
                { k: 'DF No', v: dfNoDisplay || '—' },
                { k: 'Otel', v: summaryHotelLabel },
                { k: 'Açılış', v: formData.opening_date || '—' },
                { k: 'Başlatan Departman', v: (() => {
                  const opt = selectedInitiatorDeptOption;
                  if (!opt) return '—';
                  return String(opt.label || '').replace(/\s*\([^)]+\)\s*$/, '').trim() || '—';
                })() },
                { k: 'Faaliyeti Alan', v: (() => {
                  const opt = selectedReceiverDeptOption;
                  if (!opt) return '—';
                  return String(opt.label || '').replace(/\s*\([^)]+\)\s*$/, '').trim() || '—';
                })() },
                { k: 'Kaynak', v: (() => {
                  const src = formData.nonconformity_source;
                  if (!src) return '—';
                  const opt = ncSourceOptions?.find(o => o.value === src);
                  return opt ? opt.label : src;
                })() },
                { k: 'Termin', v: formData.due_date || '—' },
                { k: 'Onaylayan', v: formData.approver_full_name || 'Atanmadı' },
              ].map((row) => (
                <div key={row.k}>
                  <dt className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 dark:text-gray-500">
                    {row.k}
                  </dt>
                  <dd className="mt-0.5 break-words font-medium text-slate-800 dark:text-gray-100">{row.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/70 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/70 sm:px-5">
          {/* Açıklama metni */}
          {workflowStage === 'initiation' && !lockInitiation ? (
            <p className="min-w-0 flex-1 truncate text-[11px] text-gray-400 dark:text-gray-500">
              Taslak: <span className="font-medium">Kaydet</span> · Aksiyona geçiş:{' '}
              <span className="font-medium">Başlatmayı tamamla</span>
            </p>
          ) : (
            <p className="min-w-0 flex-1 text-[11px] text-gray-400 dark:text-gray-500">
              Değişiklikler <span className="font-medium">Kaydet</span> ile gönderilir.
            </p>
          )}

          {/* Butonlar */}
          <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            {['approval', 'closing', 'completed', 'rejected'].includes(workflowStage) ? 'Kapat' : 'İptal'}
          </button>

          {workflowStage === 'approval' && permissions.can_approve && (
            <button
              type="button"
              disabled={transitionLoading || lockApproval}
              onClick={(e) => handleSubmit(e, {})}
              className={qmSaveButtonClassName}
            >
              Kaydet
            </button>
          )}

          {workflowStage === 'closing' && permissions.can_close && (
            <>
              <button
                type="button"
                disabled={transitionLoading || lockClosing}
                onClick={(e) => handleSubmit(e, {})}
                className={qmSaveButtonClassName}
              >
                Kaydet
              </button>
              <button
                type="button"
                disabled={transitionLoading || lockClosing}
                onClick={() => handleCloseDecision(false)}
                className="px-3 py-1.5 rounded-md border border-amber-400 text-xs text-amber-700 dark:text-amber-300 disabled:opacity-50"
              >
                Kapatılmadı
              </button>
              <button
                type="button"
                disabled={transitionLoading || lockClosing}
                onClick={() => handleCloseDecision(true)}
                className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs disabled:opacity-50"
              >
                Kapatıldı
              </button>
            </>
          )}

          {workflowStage === 'initiation' && (
            <>
              <button
                type="button"
                disabled={initiationCompleteDisabled}
                title={initiationCompleteTitle}
                onClick={(e) => handleSubmit(e, { submit_initiation: true })}
                className="px-3 py-1.5 rounded-md border border-emerald-500 text-xs text-emerald-700 dark:text-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Başlatmayı tamamla
              </button>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, {})}
                className={qmSaveButtonClassName}
              >
                Kaydet
              </button>
            </>
          )}

          {workflowStage === 'action' && (
            <>
              <button
                type="button"
                onClick={(e) => handleSubmit(e, {})}
                className={qmSaveButtonClassName}
              >
                Kaydet
              </button>
              {permissions.can_submit_action && (
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, { submit_action: true })}
                  disabled={!actionSubmitReady}
                  title={
                    !actionSubmitReady
                      ? 'Termin, kök neden ve düzeltici faaliyet tanımı veya dosya/bağlantı dolu olmalıdır'
                      : 'Tamamlanma tarihi kayıt anında otomatik atanır'
                  }
                  className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Onaya Gönder
                </button>
              )}
            </>
          )}
          </div>
        </div>
      </form>
      </div>
    </ResponsiveModal>
  );
};

export default QualityManagementFormModal;
